package org.vreo.devicehub.features.core.domain

/**
 * A sealed interface representing the result of an operation, which can either be a [Success] or an [Error].
 *
 * @param D The type of the success data.
 * @param E The type of the error, constrained to [Error].
 */
sealed interface Result<out D, out E : Error> {

    /**
     * Represents a successful result.
     *
     * @param D The type of the successful data.
     * @property data The data returned on success.
     */
    data class Success<out D>(val data: D) : Result<D, Nothing>

    /**
     * Represents a failed result.
     *
     * @param E The type of the error.
     * @property error The error associated with this result.
     */
    data class Error<out E : org.vreo.devicehub.features.core.domain.Error>(val error: E) :
        Result<Nothing, E>
}

/**
 * Maps a [Result] from one data type to another using the provided transform function if the
 * result is a Success.
 * If the result is an [Error], the same error is returned.
 *
 * @param map The transformation function to apply to the data in Success.
 * @return A new [Result] containing the mapped data or the original error.
 */
inline fun <T, E : Error, R> Result<T, E>.map(map: (T) -> R): Result<R, E> {
    return when (this) {
        is Result.Error -> Result.Error(error)
        is Result.Success -> Result.Success(map(data))
    }
}

/**
 * Converts a [Result] into an [EmptyResult], discarding the data if present.
 *
 * @return A [Result] with `Unit` as the data type if it was a Success, or the same [Error] otherwise.
 */
fun <T, E : Error> Result<T, E>.asEmptyDataResult(): EmptyResult<E> {
    return map { }
}

/**
 * Executes the given [action] if the result is a Success, and returns the original result.
 *
 * @param action A function to invoke with the success data.
 * @return The original [Result], unmodified.
 */
inline fun <T, E : Error> Result<T, E>.onSuccess(action: (T) -> Unit): Result<T, E> {
    return when (this) {
        is Result.Error -> this
        is Result.Success -> {
            action(data)
            this
        }
    }
}

/**
 * Executes the given [action] if the result is an [Error], and returns the original result.
 *
 * @param action A function to invoke with the error.
 * @return The original [Result], unmodified.
 */
inline fun <T, E : Error> Result<T, E>.onError(action: (E) -> Unit): Result<T, E> {
    return when (this) {
        is Result.Error -> {
            action(error)
            this
        }
        is Result.Success -> this
    }
}

/**
 * A type alias representing a [Result] with `Unit` as the success data.
 *
 * Useful when only success or failure matters, but no meaningful success value is returned.
 */
typealias EmptyResult<E> = Result<Unit, E>
